import { useEffect, useState } from "react";
import { Link, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "../lib/auth";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import AppleSignInButton from "../components/AppleSignInButton";
import PageMeta from "../components/PageMeta";

export default function Login() {
  const { login, user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const [remember, setRemember] = useState(false);

  // `next` query param wins over location.state.from so deep-links from
  // the claim CTA survive the redirect roundtrip.
  const nextUrl = searchParams.get("next") || location.state?.from || null;

  // Already signed in? Bounce to a sensible home instead of showing the form.
  useEffect(() => {
    if (authLoading || !user) return;
    if (nextUrl) navigate(nextUrl, { replace: true });
    else if (user.is_admin) navigate("/admin", { replace: true });
    else if (user.role === "owner") navigate("/dashboard", { replace: true });
    else navigate("/map", { replace: true });
  }, [user, authLoading, navigate, nextUrl]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await login(email, password, { remember });
    setLoading(false);
    if (res.ok) {
      toast.success("Welcome back!");
      if (res.user?.must_change_password) {
        toast("Please change your password to continue", { duration: 5000 });
        navigate("/change-password");
        return;
      }
      const to = nextUrl || location.state?.from || "/map";
      navigate(to);
    } else if (res.invitePending && res.inviteToken) {
      // Admin pre-created this account — finish onboarding instead.
      toast("Finish setting up your account to log in", { duration: 4000 });
      navigate(`/onboarding/${res.inviteToken}`);
    } else if (res.invitePending) {
      // Edge case: pending but no token returned. Fall back to lookup-by-email.
      toast("Looks like your account is pending setup — check your email or use the link we sent");
    } else {
      toast.error(res.error);
    }
  };

  const onForgotSubmit = async (e) => {
    e.preventDefault();
    if (!forgotEmail.trim()) return;
    setForgotLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: forgotEmail.trim().toLowerCase() });
      toast.success("If that email is registered, we just sent a reset link.", { duration: 6000 });
      setShowForgot(false);
      setForgotEmail("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't request reset");
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] grid place-items-center px-4 py-10" data-testid="login-page">
      <PageMeta
        title="Log In | TruckTrackr"
        description="Log in to TruckTrackr to manage your food truck schedule or follow your favorite trucks."
        canonical="https://thetrucktrackr.com/login"
      />
      <form
        onSubmit={onSubmit}
        className="w-full max-w-md rounded-2xl border border-border bg-card p-6 sm:p-8 space-y-5"
      >
        <div>
          <h1 className="font-display font-bold text-3xl tracking-tight">Welcome back</h1>
          <p className="text-sm text-muted-foreground mt-1">Log in to post spots or save favorites.</p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="email"
            data-testid="login-email-input"
          />
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label htmlFor="password">Password</Label>
            <button
              type="button"
              onClick={() => {
                setForgotEmail(email); // prefill if they've typed their email
                setShowForgot(true);
              }}
              className="text-xs text-primary hover:underline"
              data-testid="login-forgot-password"
            >
              Forgot password?
            </button>
          </div>
          <Input
            id="password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            autoComplete="current-password"
            data-testid="login-password-input"
          />
        </div>

        <label
          className="flex items-center gap-2.5 select-none cursor-pointer text-sm text-muted-foreground hover:text-foreground transition"
          data-testid="login-remember-label"
        >
          <input
            type="checkbox"
            checked={remember}
            onChange={(e) => setRemember(e.target.checked)}
            className="h-4 w-4 rounded border-input accent-primary cursor-pointer"
            data-testid="login-remember-checkbox"
          />
          <span>
            Stay logged in for <strong className="text-foreground">90 days</strong> on this device
          </span>
        </label>

        <Button
          type="submit"
          disabled={loading}
          className="w-full bg-primary text-primary-foreground hover:bg-primary/90 h-11 rounded-full"
          data-testid="login-submit-btn"
        >
          {loading ? "Signing in…" : "Sign in"}
        </Button>

        <AppleSignInButton onSuccess={() => navigate("/map", { replace: true })} />

        <p className="text-sm text-center text-muted-foreground">
          No account?{" "}
          <Link to="/signup" className="text-primary underline-offset-4 hover:underline" data-testid="login-to-signup">
            Create one
          </Link>
        </p>
      </form>

      {/* Forgot-password modal — simple one-field request */}
      {showForgot && (
        <div
          className="fixed inset-0 z-50 bg-black/60 grid place-items-center p-4"
          onClick={() => !forgotLoading && setShowForgot(false)}
          data-testid="forgot-password-modal"
        >
          <form
            onSubmit={onForgotSubmit}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md rounded-2xl bg-card border border-border p-6 space-y-4 shadow-xl"
          >
            <div>
              <h2 className="font-display font-bold text-2xl tracking-tight">Reset password</h2>
              <p className="text-sm text-muted-foreground mt-1">
                Enter your account email and we'll send you a link to set a new password.
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="forgot-email">Email</Label>
              <Input
                id="forgot-email"
                type="email"
                value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)}
                required
                autoFocus
                autoComplete="email"
                data-testid="forgot-email-input"
              />
            </div>
            <div className="flex gap-2 pt-1">
              <Button
                type="submit"
                disabled={forgotLoading}
                className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90 h-11 rounded-full"
                data-testid="forgot-submit-btn"
              >
                {forgotLoading ? "Sending…" : "Send reset link"}
              </Button>
              <Button
                type="button"
                variant="outline"
                disabled={forgotLoading}
                onClick={() => setShowForgot(false)}
                className="rounded-full"
              >
                Cancel
              </Button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
